import { ComponentProps, FC, MutableRefObject, useEffect, useRef, useState } from 'react';
import { Control, Controller, UseFormSetValue } from 'react-hook-form';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { Form, Select, Typography } from 'antd';
import { FilterFunc } from 'rc-select/es/Select';
import { Tooltip } from 'src/shared/components/tooltip';
import styled from 'styled-components';

import { Button } from '@/shared/components';
import { DrawerPickArrayOfDto } from '@/shared/components/drawers';
import {
  PickDtoFilter,
  PickDtoIsRequired,
  PickDtoOnChange,
  RenderNewButtonDeleteOptions,
} from '@/shared/lib/forms/form-wrapper.types';
import { CollectionName, CollectionsTypeValueMap } from '@/shared/lib/sj-orm/constants';
import { useSJDatabase } from '@/shared/lib/sj-orm/hooks/use-sj-database';

import { FormDeleteConfirmationModal } from '../DeleteConfirmationModal';
// import { commonfilterCollection } from './helpers';
import { commonfilterCollection } from '../helpers';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type PickDtoContextEntity = any;

export const PickArrayOfDto = <K extends CollectionName, T extends CollectionsTypeValueMap[K]>({
  label,
  required,
  control,
  collections,
  name,
  mode,
  tooltip,
  memoValue,
  // deps,
  type,
  CustomDrawer,
  setValue,
  filter = (item) => !!item,
  isRequired,
  onValueChange,
  deleteOptions,
  onDelete,
  placeholder,
  disabled,
}: {
  label: string;
  required?: boolean;
  control: Control;
  collections: CollectionName[];
  name: string;
  mode?: 'multiple' | 'tags';
  tooltip?: string;
  memoValue: MutableRefObject<Record<string, unknown>>;
  // deps?: Array<string>;
  type?: string | string[];
  CustomDrawer?: FC<ComponentProps<typeof DrawerPickArrayOfDto>>;
  setValue: UseFormSetValue<Record<string, unknown>>;
  filter?: PickDtoFilter;
  isRequired?: PickDtoIsRequired;
  onValueChange?: PickDtoOnChange;
  deleteOptions?: RenderNewButtonDeleteOptions;
  onDelete?: (fieldName: string) => void;
  placeholder?: string;
  disabled?: boolean;
}) => {
  const { t } = useTranslation(['common', 'assets', 'forms_assets', 'forms_contacts']);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const router = useRouter();

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const sjDb = useSJDatabase();

  const fieldRequired = isRequired ? isRequired(name, memoValue.current) : required;

  const dtos = collections
    .map((col) => {
      return (
        sjDb.collections[col]
          ?.findMany(commonfilterCollection)
          .filter((item) => filter(item as T, memoValue.current, name))
          .map(
            (item: {
              id: string;
              name?: string;
              nickname?: string;
              nickName?: string;
              shortname?: string;
            }) => ({
              value: item.id,
              label: item.name || item.nickName || item.nickname || item.shortname,
            }),
          ) || []
      );
    })
    .flat();

  const filterOption: FilterFunc<{
    label: string | undefined;
    value: string;
  }> = (input: string, option?: { label: string | undefined; value: string }) =>
    (option?.label ?? '').toLowerCase().includes(input.toLowerCase());
  const isValueSetted = useRef(false);

  useEffect(() => {
    if (memoValue.current[name] && !control._formValues[name]) {
      setValue(name, memoValue.current[name]);
    }
  }, [memoValue.current, name]);

  const onSelectChange = (value: unknown, fieldFn: (args: unknown) => void) => {
    memoValue.current[name] = value;
    fieldFn(value);
  };

  const onAddNewClick = async () => {
    if (!type) return;
    const collectionToCreate = collections[0];

    await router.replace({
      pathname: window.location.pathname,
      query: {
        fastAddCollection: collectionToCreate,
        fastAddTypes: type,
      },
    });
  };

  const onDeleteClick = () => {
    setIsConfirmationModalOpen(true);
  };

  const onConfirmDeleteClick = () => {
    onDelete?.(name);
    setIsConfirmationModalOpen(false);
  };

  const onCancelDeleteClick = () => {
    setIsConfirmationModalOpen(false);
  };

  /**
   * TODO coowner clean coOwnerDocument after change coOwnerEntityDtoId
   * Remove this hack after refatrorin Forms
   */
  useEffect(() => {
    const val = memoValue.current[name];

    // if (val === undefined || (val instanceof Array && val.length === 0)) {
    //   setValue(name, undefined);
    // }
    if (val === undefined || (val instanceof Array && val.length === 0)) {
      setValue(name, null);
    }
  }, [memoValue.current[name]]);

  return (
    <div>
      <Typography.Text>{label}</Typography.Text>
      {tooltip && <Tooltip title={t(tooltip)} />}
      <Wrapper>
        <Controller
          name={name}
          control={control}
          // rules={{ required, validate: compileDepsFunction }}
          rules={{ required: fieldRequired }}
          render={({ field, fieldState }) => {
            if (!field.value && memoValue.current[name] && !isValueSetted.current) {
              field.value = memoValue.current[name];
              isValueSetted.current = true;
              setValue(name, memoValue.current[name]);
            }
            return (
              <div style={{ flex: 1 }}>
                <Form.Item
                  validateStatus={fieldState.error ? 'error' : ''}
                  help={fieldState.error?.message}
                >
                  <Select
                    open={false}
                    placeholder={placeholder || label}
                    options={dtos}
                    filterOption={filterOption}
                    style={{ width: '100%' }}
                    mode={mode}
                    onClick={() => setIsDrawerOpen(true)}
                    onFocus={(event) => {
                      const rect = event.target.getBoundingClientRect();
                      const scrollTop = document.documentElement.scrollTop;
                      window.scrollTo({
                        top: rect.top + scrollTop - 40,
                        behavior: 'smooth',
                      });
                    }}
                    notFoundContent={
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          width: '100%',
                          gap: '15px',
                        }}
                      >
                        <p>There is no data added yet, fill the info in the contact section</p>
                        {!!type && <Button text="Add" onClick={onAddNewClick} />}
                      </div>
                    }
                    {...field}
                    onChange={(value) => {
                      onSelectChange(value, field.onChange);
                      onValueChange?.(name, memoValue.current);
                    }}
                    disabled={disabled}
                  />
                </Form.Item>

                {CustomDrawer ? (
                  <CustomDrawer
                    collection={collections}
                    filter={filter}
                    control={control}
                    memoValue={memoValue}
                    name={name}
                    setValue={setValue}
                    label={label}
                    isOpen={isDrawerOpen}
                    setIsOpen={setIsDrawerOpen}
                    type={type}
                    onFieldChange={(value) => {
                      field?.onChange(value);
                      onValueChange?.(name, memoValue.current);
                    }}
                    mode={mode}
                  />
                ) : (
                  <DrawerPickArrayOfDto
                    collection={collections}
                    filter={filter}
                    control={control}
                    memoValue={memoValue}
                    name={name}
                    setValue={setValue}
                    label={label}
                    isOpen={isDrawerOpen}
                    setIsOpen={setIsDrawerOpen}
                    type={type}
                    onFieldChange={(value: unknown) => {
                      field?.onChange(value);
                      onValueChange?.(name, memoValue.current);
                    }}
                    mode={mode}
                  />
                )}
              </div>
            );
          }}
        />

        {deleteOptions && (
          <FormDeleteConfirmationModal
            deleteOptions={deleteOptions}
            isOpen={isConfirmationModalOpen}
            onCancelDeleteClick={onCancelDeleteClick}
            onConfirmDeleteClick={onConfirmDeleteClick}
            onDeleteClick={onDeleteClick}
          />
        )}
      </Wrapper>
    </div>
  );
};

const Wrapper = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;
  flex: 1;
  column-gap: 8px;
`;
